import React, { useEffect, useState, useContext } from 'react';
import { Row, Col, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import LoadingPage from '../LoadingPage';
import ApiService from '../../services/ApiService';
import MiniGallery from '../attachments/MiniGallery';
import ModalContext from './ModalContext';
import ModalList from './ModalList';
import ModalTable from './ModalTable';
import SectionHeading from './SectionHeading';
import { popProperty } from '../../util/general';
import RenderMd from '../../util/markdown';

const partHistoryHeaders = [
  {
    text: 'Invoice',
    width: 90,
    className: 'text-center',
    helper: 'viewInvoice',
  },
  {
    text: 'Date',
    width: 90,
    className: 'text-center',
  },
  {
    text: 'Qty',
    width: 70,
    className: 'text-center',
  },
  {
    text: 'Net',
    width: 65,
    className: 'text-right',
  },
  {
    text: 'Customer',
    width: 85,
    className: 'text-center',
    helper: 'customerDetail',
  },
  {
    text: 'Name',
    width: 200,
    helper: 'customerDetail',
  },
];

const partReceivedHeaders = [
  {
    text: 'Invoice',
    width: 90,
    className: 'text-center',
    helper: 'purchaseOrderDetail',
  },
  {
    text: 'Date',
    width: 90,
    className: 'text-center',
  },
  {
    text: 'Qty',
    width: 60,
    className: 'text-center',
  },
  {
    text: 'Net',
    width: 75,
    className: 'text-right',
  },
  {
    text: 'Vendor',
    width: 150,
    className: 'text-center',
  },
];

const fifoHeaders = [
  {
    text: 'Invoice',
    width: 120,
    className: 'text-center',
    helper: 'purchaseOrderDetail',
  },
  {
    text: 'Date',
    width: 90,
    className: 'text-center',
  },
  {
    text: 'Qty',
    width: 65,
    className: 'text-right',
  },
  {
    text: 'Net',
    width: 85,
    className: 'text-right',
  },
];

const pendingHeaders = [
  {
    text: 'Purchase Order',
    width: 120,
    className: 'text-center',
    helper: 'purchaseOrderDetail',
  },
  {
    text: 'Line',
    width: 65,
    className: 'text-center',
  },
  {
    text: 'Qty',
    width: 65,
    className: 'text-right',
  },
  {
    text: 'Date',
    width: 85,
    className: 'text-center',
  },
  {
    text: 'Order Type',
    width: 85,
    className: 'text-center',
  },
];

const PartDetail = ({ lookupKey, view }) => {
  const [loading, setLoading] = useState(true);
  const [primaryData, setPrimaryData] = useState({});
  const [yearHeaders, setYearHeaders] = useState({});
  const [hitHeaders, setHitHeaders] = useState({});

  const { setTitle } = useContext(ModalContext);

  const getData = () => {
    setLoading(true);

    ApiService.get(`drilldown/${view}/${encodeURIComponent(lookupKey)}`).then(response => {
      const { data } = response.data;

      const specs = JSON.parse(data);

      const recv = [];
      for (const row of specs.received) {
        const line = [];
        Object.keys(row).forEach(key => {
          line.push(row[key]);
        });
        recv.push(line);
      }
      specs.received = recv;

      const sales = [];
      for (const row of specs.sales) {
        const line = [];
        Object.keys(row).forEach(key => {
          line.push(row[key]);
        });
        sales.push(line);
      }
      specs.sales = sales;

      const fifo = [];
      for (const row of specs.fifo) {
        const line = [];
        Object.keys(row).forEach(key => {
          line.push(row[key]);
        });
        fifo.push(line);
      }
      specs.fifo = fifo;

      const pending = [];
      for (const row of specs.pendingOrders) {
        const line = [];
        Object.keys(row).forEach(key => {
          line.push(row[key]);
        });
        pending.push(line);
      }
      specs.pendingOrders = pending;

      const sal_per_year = [];
      for (const row of specs.salesHistory) {
        const line = [];
        Object.keys(row).forEach(key => {
          line.push(row[key]);
        });
        sal_per_year.push(line);
      }
      setYearHeaders(specs.salesHistory.length ? Object.keys(specs.salesHistory[0]) : []);
      specs.salesHistory = sal_per_year;

      const hits_per_year = [];
      for (const row of specs.greatestHits) {
        const line = [];
        Object.keys(row).forEach(key => {
          line.push(row[key]);
        });
        hits_per_year.push(line);
      }
      setHitHeaders(specs.greatestHits.length ? Object.keys(specs.greatestHits[0]) : []);
      specs.greatestHits = hits_per_year;

      setPrimaryData(specs);
      setLoading(false);
    });
  };

  useEffect(() => {
    getData();
    setTitle && setTitle(`Part Full Detail ${lookupKey}`); // eslint-disable-line
  }, []);

  if (loading) {
    return <LoadingPage />;
  }

  const [stockClass, primaryLocation, secondaryLocation, description, orderControl, inventory, portalText] =
    popProperty(primaryData.partAttributes, [
      'Stock Class',
      'Primary Location',
      'Secondary Locations',
      'Short / Long Description',
      'Order Control',
      'Physical Inventory',
      'Portal Text',
    ]);

  return (
    <div>
      <Row>
        <Col md={6}>
          <MiniGallery itemName="PIV1" itemId={lookupKey} />
          <div>
            <Badge variant="primary" className="p-2 mr-1" title="Primary Location">
              {primaryLocation}
            </Badge>
            <div className="float-right">
              {secondaryLocation &&
                secondaryLocation.split(' ').map(bin => {
                  return (
                    <Badge variant="secondary" className="p-2 mr-1" key={bin} title="Secondary Location">
                      {bin}
                    </Badge>
                  );
                })}
            </div>
          </div>

          <SectionHeading>Received Parts</SectionHeading>
          <ModalTable
            hideToolbar
            headers={partReceivedHeaders}
            data={primaryData.received}
            style={{ height: '300px' }}
          />

          <SectionHeading>Part Sales</SectionHeading>
          <ModalTable hideToolbar headers={partHistoryHeaders} data={primaryData.sales} style={{ height: '300px' }} />
        </Col>

        <Col md={6}>
          <div className="entity-title">
            <div>
              <Badge variant="success" className="h6 float-left p-2 mb-0">
                {stockClass}
              </Badge>
            </div>
            <div className="title mb-0">{lookupKey}</div>
          </div>
          <hr className="m-1" />
          <div className="mb-1">{description}</div>
          <ModalList data={primaryData.partAttributes} columns={2} className="mb-2" />

          {orderControl && (
            <>
              <SectionHeading size="sm">Order Control</SectionHeading>
              <div className="pl-1 mb-2" style={{ backgroundColor: 'var(--yellow-0)' }}>
                {orderControl}
              </div>
            </>
          )}

          {inventory && (
            <>
              <SectionHeading size="sm">Physical Inventory</SectionHeading>
              <div className="pl-1 mb-2" style={{ backgroundColor: 'var(--yellow-0)' }}>
                {inventory}
              </div>
            </>
          )}

          <SectionHeading size="sm">Prices</SectionHeading>
          <Row className="mr-0 ml-0 mb-1">
            {Object.keys(primaryData.price).map(key => {
              return (
                <React.Fragment key={key}>
                  <Col sm={3} className="p-0">
                    <div
                      className="p-1 m-1 text-bold"
                      style={{ backgroundColor: 'var(--primary-color)', color: 'var(--primary-fore)' }}
                    >
                      {key}
                    </div>
                  </Col>
                  <Col sm={3} className="p-0 text-right">
                    <div className="p-1 m-1" style={{ backgroundColor: '#eee' }}>
                      {primaryData.price[key]}
                    </div>
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>

          <SectionHeading size="sm">Dates</SectionHeading>
          <Row className="mr-0 ml-0 mb-1">
            {Object.keys(primaryData.dates).map(key => {
              return (
                <React.Fragment key={key}>
                  <Col sm={3} className="p-0">
                    <div
                      className="p-1 m-1 text-bold"
                      style={{ backgroundColor: 'var(--primary-color)', color: 'var(--primary-fore)' }}
                    >
                      {key}
                    </div>
                  </Col>
                  <Col sm={3} className="p-0 text-right">
                    <div className="p-1 m-1" style={{ backgroundColor: '#eee' }}>
                      {primaryData.dates[key]}
                    </div>
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>

          <SectionHeading size="sm">Recent Sales</SectionHeading>
          <Row className="mr-0 ml-0 mb-1">
            {Object.keys(primaryData.recentSales).map(key => {
              return (
                <React.Fragment key={key}>
                  <Col sm={3} className="p-0">
                    <div
                      className="p-1 m-1 text-bold"
                      style={{ backgroundColor: 'var(--primary-color)', color: 'var(--primary-fore)' }}
                    >
                      {key}
                    </div>
                  </Col>
                  <Col sm={3} className="p-0 text-right">
                    <div className="p-1 m-1" style={{ backgroundColor: '#eee' }}>
                      {primaryData.recentSales[key]}
                    </div>
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <SectionHeading>Sales</SectionHeading>
          <ModalTable hideToolbar headers={yearHeaders} data={primaryData.salesHistory} style={{ height: '70px' }} />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <SectionHeading>Hits</SectionHeading>
          <ModalTable hideToolbar headers={hitHeaders} data={primaryData.greatestHits} style={{ height: '70px' }} />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <SectionHeading>FIFO</SectionHeading>
          <ModalTable hideToolbar headers={fifoHeaders} data={primaryData.fifo} style={{ height: '300px' }} />
        </Col>
        <Col md={6}>
          <SectionHeading>Pending Orders</SectionHeading>
          {/* <ModalTable hideToolbar headers={pendingHeaders} data={primaryData.pendingOrders} style={{ height: '150px' }} /> */}
          <ModalTable
            hideToolbar
            headers={pendingHeaders}
            data={primaryData.pendingOrders}
            style={{ height: '300px' }}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RenderMd>{portalText}</RenderMd>
        </Col>
      </Row>
    </div>
  );
};

PartDetail.propTypes = {
  lookupKey: PropTypes.string,
  view: PropTypes.string,
};

export default PartDetail;
